import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Pagination from '@/components/base/Pagination';
import SectionHeader from '@/components/base/SectionHeader';
import { EmptyState, LoadingState } from '@/components/base/StateBlocks';
import VideoCard from '@/components/feature/VideoCard';
import { categories, hotSearchKeywords } from '@/mocks/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useSimulatedLoad } from '@/hooks/useSimulatedLoad';
import { PAGE_SIZE, searchVideos } from '@/utils/videos';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const [input, setInput] = useState(keyword);
  const { loading } = useSimulatedLoad(400);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useDocumentMeta({
    title: keyword ? `搜索结果 - '${keyword}' - 红桃视频` : '搜索视频 - 红桃视频',
    description: keyword
      ? `红桃视频中与「${keyword}」相关的视频搜索结果，快速找到想看的电影、电视剧与综艺。`
      : '在红桃视频搜索电影、电视剧、综艺等高清视频内容。',
    keywords: keyword ? `${keyword},搜索,红桃视频,在线观看` : '红桃视频,搜索视频,在线观看',
  });

  const results = useMemo(() => (keyword ? searchVideos(keyword) : []), [keyword]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const paged = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const runSearch = (kw: string, nextPage = 1) => {
    const params: Record<string, string> = {};
    if (kw.trim()) params.q = kw.trim();
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    runSearch(input);
  };

  const handlePageChange = (next: number) => {
    runSearch(keyword, next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <div className="max-w-3xl">
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
          {keyword ? '搜索结果' : '搜索视频'}
        </h1>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row items-stretch gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片名、类型或关键词"
              className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 transition"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        {keyword && (
          <p className="mt-4 text-sm text-foreground-600">
            关键词「<span className="text-accent-400 font-semibold">{keyword}</span>」共找到{' '}
            <span className="text-accent-400 font-semibold">{results.length}</span> 条结果
          </p>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8">
        <div className="min-w-0">
          {!keyword ? (
            <EmptyState
              title="输入关键词开始搜索"
              description="可以试试搜索影片名称、类型或主演，例如「悬疑」「科幻」"
              icon="ri-search-line"
            />
          ) : loading ? (
            <LoadingState text="正在搜索…" />
          ) : paged.length === 0 ? (
            <EmptyState
              title="未找到相关视频"
              description="您可以尝试其他关键词，或从右侧热门搜索中选择"
              icon="ri-emotion-sad-line"
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
              {paged.map((video) => (
                <VideoCard key={video.id} video={video} showSynopsis />
              ))}
            </div>
          )}

          {keyword && !loading && paged.length > 0 && (
            <div className="mt-10">
              <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">热门搜索</h2>
            <div className="flex flex-wrap gap-2">
              {hotSearchKeywords.map((kw) => (
                <Link
                  key={kw}
                  to={`/search?q=${encodeURIComponent(kw)}`}
                  className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
                    kw === keyword
                      ? 'bg-primary-500 text-background-950 font-semibold'
                      : 'bg-background-200/60 text-foreground-700 hover:bg-background-300/60'
                  }`}
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
            <SectionHeader title="按分类浏览" icon="ri-apps-2-line" />
            <ul className="flex flex-col gap-1 -mt-2">
              {categories.map((cat) => (
                <li key={cat.key}>
                  <Link
                    to={cat.path}
                    className="flex items-center gap-3 py-2.5 group"
                  >
                    <span className="w-9 h-9 rounded-md bg-primary-500/12 flex items-center justify-center text-primary-400 group-hover:bg-primary-500 group-hover:text-background-950 transition-colors">
                      <i className={`${cat.icon} text-lg`} />
                    </span>
                    <span className="text-sm text-foreground-800 group-hover:text-primary-400 transition-colors">
                      {cat.name}
                    </span>
                    <i className="ri-arrow-right-s-line ml-auto text-foreground-500" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}